import { useEffect, useMemo } from 'react'
import { StudioRoute, useCreateRoute } from '@/lib/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { useProject } from '@/app/routes/Dashboard/Dashboard.hooks'
import { useZAFClient, isZendeskApp } from '@/features/zendesk-app'
import { FormikConfig } from 'formik'
import { CreateRouteArgs, getRouteTypes } from '@/lib/knots-api'
import { AutoTemplateFormType, ZendeskTemplateAppMetadata } from './AutoTemplateRoute.types'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { secretsQueries } from '@/features/secrets'
import { connectorsQueries } from '@/features/connectors'
import { templatesQueries } from '@/features/templates'

const INITIALIZED_VALUE = 'true'

export const useTemplateId = (): number => {
  const location = useLocation()

  return useMemo(() => {
    const params = new URLSearchParams(location.search)
    return parseInt(params.get('template_id') ?? '0')
  }, [location.search])
}

export const useRedirectInitializedZendeskApp = () => {
  const zafClient = useZAFClient()
  const navigate = useNavigate()

  const isInitialized = async () => {
    if (!zafClient) return true
    const { settings } = await zafClient.metadata<ZendeskTemplateAppMetadata>()
    return settings.initialized === INITIALIZED_VALUE
  }

  const redirectIfInitialized = async () => {
    if (isZendeskApp() && (await isInitialized())) {
      navigate('/dashboard')
    }
  }

  useEffect(() => {
    redirectIfInitialized().then()
  }, [])
}

export const useInitializeZendeskApp = () => {
  const zafClient = useZAFClient()

  return async () => {
    if (!zafClient) return
    const { installationId } = await zafClient.metadata<ZendeskTemplateAppMetadata>()

    await zafClient.request({
      url: `/api/v2/apps/installations/${installationId}`,
      type: 'PUT',
      contentType: 'application/json',
      data: JSON.stringify({ settings: { initialized: INITIALIZED_VALUE } }),
    })
  }
}

export const usePreloadTemplateFormQueries = (templateId: number) => {
  const queryClient = useQueryClient()
  const {
    data: { template },
  } = useSuspenseQuery(templatesQueries.detail(templateId))
  const [project] = useProject()

  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: ['route-types', template.type],
      queryFn: () => getRouteTypes(template.type),
      staleTime: Infinity,
    })
    queryClient.prefetchQuery(connectorsQueries.list())
    queryClient.prefetchQuery(secretsQueries.list(project.id!))
  }, [])
}

export const useAutoTemplateForm = (templateId: number): FormikConfig<AutoTemplateFormType> => {
  const [project] = useProject()
  const createRoute = useCreateRoute(project.id!)
  const navigate = useNavigate()
  const {
    data: { template },
  } = useSuspenseQuery(templatesQueries.detail(templateId))
  const initializeZendeskApp = useInitializeZendeskApp()

  const requirementKeys = useMemo(() => {
    const secrets = template.requirements?.secrets ?? []
    const connectors = template.requirements?.connectors ?? []
    const params = template.requirements?.params ?? []
    return secrets
      .concat(connectors)
      .concat(params)
      .map((requirement) => requirement.key)
  }, [template])

  const getRequirementsPayload = (values: AutoTemplateFormType): CreateRouteArgs['requirements'] => {
    return {
      connectors: template.requirements?.connectors?.reduce(
        (connectors, { key }) => ({
          ...connectors,
          [key]: values[key],
        }),
        {}
      ),
      secrets: template.requirements?.secrets?.reduce(
        (secrets, { key }) => ({
          ...secrets,
          [key]: values[key],
        }),
        {}
      ),
      params: template.requirements?.params?.reduce(
        (secrets, { key }) => ({
          ...secrets,
          [key]: typeof values[key] === 'object' ? JSON.stringify(values[key]) : values[key],
        }),
        {}
      ),
    }
  }

  return {
    initialValues: {},
    validate: (values: AutoTemplateFormType) => {
      const errors: Record<string, string> = {}

      requirementKeys.forEach((key) => {
        if (values[key] !== undefined && values[key] !== '') return
        errors[key] = 'Value should be present'
      })

      return errors
    },
    onSubmit: async (values) => {
      const payload = {
        route: {} as StudioRoute,
        templateId,
        requirements: getRequirementsPayload(values),
      }

      return createRoute.mutateAsync(payload, {
        onSuccess: async (route) => {
          if (isZendeskApp()) await initializeZendeskApp()
          navigate(`/projects/${project.id}/${route.id}`, { replace: true })
        },
      })
    },
  }
}
